import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import i18n from '../../lib/i18n';
import { showToast, validationRemoteSendTextInfo } from '../../lib/tools';
import Color from '../../styles/common/Color';
import config from '../../config';
import imgSave from '../../images/remote/save.svg';
import imgSend from '../../images/remote/send.svg';
import imgClose from '../../images/common/cancel_black.svg';
import Textarea from '../../styles/common/Textarea';
import { RequiredChip } from '../Parts/Chip';
import { MAX_LENGTH } from '../../lib/constants';
import ColorSF2 from '../../styles/common/ColorSF2';

const { colors } = config.blackboardParam;

const MainBlock = styled.div`
  display: flex;
  flex-flow: column;
  overflow-y: auto;
  width: 310px;
  height: 100vh;
  padding: 12px;
  padding-bottom: 50px;
  background-color: ${ColorSF2.skyBlue80};
`;
const CloseButton = styled.div`
  cursor: pointer;
  top: 10px;
  right: 10px;
  > img {
    width: 15px;
    height: 15px;
  }
`;
const Contents = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
`;
const Border = styled.div`
  height: 1px;
  width: 280px;
  margin: 0 auto;
  background-color: ${ColorSF2.black}};
`;
const TitleWrapper = styled.div`
  width: 100%;
  height: 15px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
`;
const TitleLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
`;
const SubTitleLabel = styled.div`
  font-size: 12px;
  padding-top: 8px;
  padding-bottom: 4px;
  font-weight: bold;
`;
const Item = styled.div`
  display: flex;
  width: 100%;
`;
const ItemLabel = styled.div`
  flex: none;
  width: 100px;
  font-size: 12px;
  display: flex;
  line-height: ${props => (props.name === 'design' ? '36px' : '')};
  line-height: ${props => (props.name === 'letters' ? '30px' : '')};
`;
const MsgLabel = styled.div`
  font-size: 10px;
`;
const ShowRadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  > input {
    cursor: pointer;
  }
  > label {
    font-size: 12px;
    padding-right: 10px;
  }
`;
const ColorRadioGroup = styled.div`
  display: flex;
  align-items: center;
  line-height: 37px;
  gap: 6px;
  > input {
    display: none;
  }
  > label {
    &::before {
      content: '';
      display: inline-block;
      background-size: contain;
      width: 25px;
      height: 25px;
      vertical-align: middle;
    }
  }
  > input:checked + div {
    border: solid ${ColorSF2.white};
    box-sizing: border-box;
  }
`;
const ColorRadio = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  background-color: ${props => props.color};
  > label {
    cursor: pointer;
    font-size: 10px;
    font-weight: bold;
  }
`;
const OpacityRangeGroup = styled.div`
  display: flex;
  align-items: center;
  > input {
    cursor: pointer;
  }
  > label {
    width: 42px;
    font-size: 11px;
  }
`;
const PositionRadioGroup = styled.div`
  display: grid;
  grid-template-columns: 40px 24px;
  grid-template-rows: 24px 24px 40px;
  gap: 4px;
  padding: 12px 0px 12px 0px;
  > div {
    > input {
      cursor: pointer;
      width: 100%;
      position: relative;
      vertical-align: middle;
      top: 9px;
    }
    > label {
      pointer-events: none;
      position: relative;
      text-align: center;
      font-size: 8px;
    }
    .left_top {
      top: -30px;
      left: 9px;
    }
    .right_top {
      top: -30px;
      left: 9px;
    }
    .left {
      top: -12.5px;
      left: -3px;
    }
    .right {
      top: -12.5px;
      left: 33px;
    }
    .left_bottom {
      top: 6px;
      left: 9px;
    }
    .right_bottom {
      top: 6px;
      left: 9px;
    }
  }
`;
const LettersInputText = styled.input.attrs({ type: 'text' })`
  width: 100%;
  margin: 4px 0;
  border-color: ${props => (props.isError ? Color.red : 'none')};
`;
const LettersTextarea = styled(Textarea)`
  width: 100%;
  height: 70px;
`;
const LettersSelector = styled.select`
  width: 80px;
  margin: 4px 0;
`;
const LettersButtonGroup = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 4px;
  padding-top: 10px;
`;
const LettersButton = styled.div`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  display: flex;
  width: 100px;
  height: 30px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0);
  color: ${ColorSF2.navyBlue};
  border: 1px solid ${ColorSF2.navyBlue};
  border-radius: 5px;
  gap: 4px;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  > label {
    cursor: ${props => (props.disabled ? 'default' : 'pointer')};
    font-size: 11px;
  }
  > img {
    width: 18px;
    height: 18px;
  }
  :hover {
    opacity: 0.5;
  }
`;

function createColorRadio(key, message, checkedColor, onSendColor) {
  const isCheked = checkedColor === key;
  return [
    <input
      key={`input${key}`}
      type="radio"
      name="color"
      checked={isCheked}
      onChange={() => {}}
    />,
    <ColorRadio
      key={`color${key}`}
      color={colors[key]}
      onClick={() => onSendColor(key)}
    >
      <label style={{ color: ColorSF2.white }}>{message}</label>
    </ColorRadio>,
  ];
}

function createPositionRadio(key, message, checkedPosition, onSendPosition) {
  const isCheked = checkedPosition === key;
  return (
    <div key={key}>
      <input
        type="radio"
        name="position"
        onChange={() => onSendPosition(key)}
        checked={isCheked}
      />
      <label className={key}>{message}</label>
    </div>
  );
}

/**
 * 黒板の設定を表示するダイアログ。編集も可能。
 *
 * @component
 * @param {Object} props - コンポーネントのプロパティ
 * @param {string} props.locale - ロケール文字列(例: ja, en)
 * @param {Object} props.blackboardInfo - 黒板の設定情報
 * @param {Object} props.sendTextInfo - 送信テキストの情報
 * @param {Function} props.onClose - 閉じるボタンのクリックイベントハンドラ
 * @param {Function} props.onSendShow - 表示・非表示の設定変更時のイベントハンドラ
 * @param {Function} props.onSendColor - 色の設定変更時のイベントハンドラ
 * @param {Function} props.onSendOpacity - 透明度の設定変更時のイベントハンドラ
 * @param {Function} props.onSendPosition - 位置の設定変更時のイベントハンドラ
 * @param {Function} props.onUpdateText - テキスト情報更新時のイベントハンドラ
 * @param {Function} props.onCreateBlackboard - 黒板作成時のイベントハンドラ
 * @param {Function} props.onSendBlackboardText - 黒板テキスト送信イベントハンドラ
 * @param {boolean} props.sendButtonDisabled - 送信ボタンの有効/無効フラグ
 * @returns {ReactElement} RemoteBlackboard コンポーネント
 */
const RemoteBlackboard = ({
  locale,
  blackboardInfo,
  sendTextInfo,
  onClose,
  onSendShow,
  onSendColor,
  onSendOpacity,
  onSendPosition,
  onUpdateText,
  onCreateBlackboard,
  onSendBlackboardText,
  sendButtonDisabled,
}) => {
  const { isShow, color, opacity, position } = blackboardInfo;
  const { title, titleSuffix, subtitle, note } = sendTextInfo;
  const intl = useIntl();
  const { formatMessage } = intl;
  const i18nMessages = i18n[locale];
  const suffixes = i18nMessages['remotePage.bs.label.letters.suffixes'];
  const colorMessages = i18nMessages['remotePage.bs.label.design.bgColors'];
  const positionMessages = i18nMessages['remotePage.bs.label.design.positions'];
  const suffixOptions = () =>
    Object.entries(suffixes).map(params => {
      const [key, value] = params;
      return (
        <option value={key} key={`suffixOptions_${key}`}>
          {value}
        </option>
      );
    });
  return (
    <MainBlock>
      {/* タイトル */}
      <TitleWrapper>
        <TitleLabel>
          <FormattedMessage id="remotePage.bs.label.title" />
        </TitleLabel>
        <CloseButton onClick={onClose}>
          <img src={imgClose} alt="onClose" />
        </CloseButton>
      </TitleWrapper>
      {/* コンテンツ１ */}
      <Contents>
        <SubTitleLabel>
          <FormattedMessage id="remotePage.bs.label.design" />
        </SubTitleLabel>
        <Item>
          <ItemLabel name="design">
            <FormattedMessage id="remotePage.bs.label.design.showHide" />
          </ItemLabel>
          <ShowRadioGroup>
            <input
              type="radio"
              name="showHide"
              onChange={() => onSendShow(true)}
              checked={isShow}
            />
            <label>
              <FormattedMessage id="remotePage.bs.label.design.showHide.show" />
            </label>
            <input
              type="radio"
              name="showHide"
              onChange={() => onSendShow(false)}
              checked={!isShow}
            />
            <label>
              <FormattedMessage id="remotePage.bs.label.design.showHide.hide" />
            </label>
          </ShowRadioGroup>
        </Item>
        <Item>
          <ItemLabel name="design">
            <FormattedMessage id="remotePage.bs.label.design.bgColor" />
          </ItemLabel>
          <ColorRadioGroup>
            {Object.keys(colors).map(key =>
              createColorRadio(key, colorMessages[key], color, onSendColor),
            )}
          </ColorRadioGroup>
        </Item>
        <Item>
          <ItemLabel name="design">
            <FormattedMessage id="remotePage.bs.label.design.opacity" />
          </ItemLabel>
          <OpacityRangeGroup>
            <label>
              <FormattedMessage
                id="remotePage.bs.label.design.opacity.value"
                values={{ opacity }}
              />
            </label>
            <input
              type="range"
              name="opacity"
              min="0"
              max="100"
              value={opacity}
              onChange={e => onSendOpacity(e.target.value)}
            />
          </OpacityRangeGroup>
        </Item>
        <Item>
          <ItemLabel name="design">
            <FormattedMessage id="remotePage.bs.label.design.position" />
          </ItemLabel>
          <PositionRadioGroup>
            {Object.keys(positionMessages).map(key =>
              createPositionRadio(
                key,
                positionMessages[key],
                position,
                onSendPosition,
              ),
            )}
          </PositionRadioGroup>
        </Item>
      </Contents>
      <Border />
      {/* コンテンツ２ */}
      <Contents>
        <SubTitleLabel>
          <FormattedMessage id="remotePage.bs.label.letters" />
        </SubTitleLabel>
        <MsgLabel>
          <FormattedMessage id="remotePage.bs.label.letters.msg" />
        </MsgLabel>
        <Item>
          <ItemLabel name="letters">
            <FormattedMessage id="remotePage.bs.label.letters.title" />
            <RequiredChip />
          </ItemLabel>
          <LettersInputText
            name="title"
            value={title.value}
            onChange={e => onUpdateText({ title: e.target.value })}
            isError={title.error}
            maxLength={MAX_LENGTH.text}
          />
        </Item>
        <Item>
          <ItemLabel name="letters">
            <FormattedMessage id="remotePage.bs.label.letters.suffix" />
          </ItemLabel>
          <LettersSelector
            name="suffix"
            value={titleSuffix.value}
            onChange={e => {
              onUpdateText({
                titleSuffix: e.target.value !== 'none' ? e.target.value : '',
              });
            }}
          >
            {suffixOptions()}
          </LettersSelector>
        </Item>
        <Item>
          <ItemLabel name="letters">
            <FormattedMessage id="remotePage.bs.label.letters.subTitle" />
          </ItemLabel>
          <LettersInputText
            name="subTitle"
            value={subtitle.value}
            onChange={e => onUpdateText({ subtitle: e.target.value })}
            maxLength={MAX_LENGTH.text}
          />
        </Item>
        <Item>
          <ItemLabel name="letters">
            <FormattedMessage id="remotePage.bs.label.letters.remarks" />
          </ItemLabel>
          <LettersTextarea
            value={note.value}
            onChange={e => onUpdateText({ note: e.target.value })}
            maxLength={MAX_LENGTH.text}
          />
        </Item>
        <LettersButtonGroup>
          <LettersButton
            role="presentation"
            onClick={() => {
              const result = validationRemoteSendTextInfo('title', title.value);
              if (!result.title.error) {
                onCreateBlackboard({
                  title: title.value,
                  titleSuffix: titleSuffix.value,
                  subtitle: subtitle.value,
                  note: note.value,
                });
                showToast(
                  formatMessage({ id: 'remotePage.bs.toast.letters.save' }),
                );
              } else {
                onUpdateText({ title: title.value });
              }
            }}
          >
            <img src={imgSave} alt="onSave" />
            <label>
              <FormattedMessage id="remotePage.bs.label.letters.save" />
            </label>
          </LettersButton>
          <LettersButton
            disabled={sendButtonDisabled}
            role="presentation"
            onClick={() => {
              const result = validationRemoteSendTextInfo('title', title.value);
              if (!result.title.error) {
                onSendBlackboardText();
                showToast(
                  formatMessage({ id: 'remotePage.bs.toast.letters.send' }),
                );
              } else {
                onUpdateText({ title: title.value });
              }
            }}
          >
            <img src={imgSend} alt="onSend" />
            <label>
              <FormattedMessage id="common.label.send" />
            </label>
          </LettersButton>
        </LettersButtonGroup>
      </Contents>
    </MainBlock>
  );
};

RemoteBlackboard.propTypes = {
  locale: PropTypes.string.isRequired,
  blackboardInfo: PropTypes.object.isRequired,
  sendTextInfo: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSendShow: PropTypes.func.isRequired,
  onSendColor: PropTypes.func.isRequired,
  onSendOpacity: PropTypes.func.isRequired,
  onSendPosition: PropTypes.func.isRequired,
  onUpdateText: PropTypes.func.isRequired,
  onCreateBlackboard: PropTypes.func.isRequired,
  onSendBlackboardText: PropTypes.func.isRequired,
  sendButtonDisabled: PropTypes.bool.isRequired,
};

export default RemoteBlackboard;
